import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import launchSession, { launchFilterKeys } from '../services/launchSession';
import { Study, StudyPage } from '../services/clinicalApi';
import { ErrorMessage, errorText, Pagination, ReportView, statusText } from './RecordDetails';

export default function LaunchPage({
  renderViewer,
}: {
  renderViewer: (key: string) => React.ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const grant = useSyncExternalStore(launchSession.subscribe, launchSession.getSnapshot);
  const [input] = useState(() => {
    const query = new URLSearchParams(location.search);
    const filters: Record<string, string> = {};
    launchFilterKeys.forEach(key => {
      const value = query.get(key)?.trim();
      if (value) {
        filters[key] = value;
      }
    });
    return { ticket: query.get('ticket') || query.get('token') || '', filters };
  });
  const [result, setResult] = useState<StudyPage>({ studies: [], total: 0 });
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(true);
  const [selected, setSelected] = useState<Study | null>(null);
  const [tab, setTab] = useState('report');
  const [imageKey, setImageKey] = useState('');
  const [imageBusy, setImageBusy] = useState(false);
  const [error, setError] = useState('');
  const request = useRef<AbortController | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    // Remove ticket and patient filters before any report/DICOM request.
    navigate('/clinical/launch', { replace: true });
    launchSession
      .exchange(input.ticket, input.filters, controller.signal)
      .then(value => {
        if (controller.signal.aborted) {
          return;
        }
        setResult(value);
        setSelected(value.studies[0] || null);
      })
      .catch(error => {
        if (!controller.signal.aborted) {
          setError(errorText(error));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setBusy(false);
        }
      });
    return () => {
      controller.abort();
      request.current?.abort();
      launchSession.clearToken();
    };
  }, [input, navigate]);
  async function changePage(next: number) {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setBusy(true);
    setError('');
    try {
      const value = await launchSession.api.studies({}, next, controller.signal);
      if (!controller.signal.aborted) {
        setResult(value);
        setPage(next);
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        setError(errorText(error));
      }
    }
    if (!controller.signal.aborted) {
      setBusy(false);
    }
  }
  function select(study: Study) {
    // Clicking the already loaded exam keeps its active tab and viewport.
    if (imageKey && selected?.register_key === study.register_key) {
      return;
    }
    request.current?.abort();
    setImageBusy(false);
    setImageKey('');
    setSelected(study);
    setTab('report');
    setError('');
    navigate('/clinical/launch', { replace: true });
  }
  async function showImages() {
    if (!selected || imageBusy) {
      return;
    }
    if (imageKey === selected.register_key) {
      setTab('image');
      window.dispatchEvent(new Event('resize'));
      return;
    }
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setImageBusy(true);
    setError('');
    try {
      const uids = await launchSession.api.uids(selected, controller.signal);
      if (controller.signal.aborted) {
        return;
      }
      const query = new URLSearchParams();
      uids.forEach(uid => query.append('StudyInstanceUIDs', uid));
      navigate({ pathname: '/clinical/launch', search: query.toString() }, { replace: true });
      setImageKey(selected.register_key);
      setTab('image');
    } catch (error) {
      if (!controller.signal.aborted) {
        setError(errorText(error));
      }
    }
    if (!controller.signal.aborted) {
      setImageBusy(false);
    }
  }
  if (busy && !grant) {
    return (
      <main className="clinical-launch-state">
        <h1>临床检查查阅</h1>
        <p role="status">正在验证链接授权…</p>
      </main>
    );
  }
  if (!grant) {
    return (
      <main
        className="clinical-launch-state"
        data-cy="clinical-launch-denied"
      >
        <h1>无法打开检查</h1>
        <ErrorMessage message={error || '直达链接授权已过期，请从医院系统重新打开。'} />
      </main>
    );
  }
  return (
    <main
      className="clinical-launch"
      data-cy="clinical-launch"
    >
      <aside data-cy="clinical-launch-sidebar">
        <header>
          <h1>检查记录</h1>
          <button onClick={launchSession.clearToken}>结束查阅</button>
        </header>
        {busy && <p role="status">正在查询检查…</p>}
        {!result.studies.length && <p>授权范围内暂无检查</p>}
        <ul
          className="clinical-record-list"
          data-cy="clinical-launch-list"
        >
          {result.studies.map(study => (
            <li key={study.register_key}>
              <button
                disabled={busy}
                className={selected?.register_key === study.register_key ? 'selected' : ''}
                onClick={() => select(study)}
                data-cy="clinical-launch-record"
                aria-current={selected?.register_key === study.register_key ? 'true' : undefined}
              >
                <span className="clinical-record-heading">
                  <strong title={study.chinese_name || '姓名未知'}>
                    {study.chinese_name || '姓名未知'}
                  </strong>
                  <span className="clinical-record-status">{statusText(study.state)}</span>
                </span>
                <span className="clinical-record-exam">
                  <span className="clinical-record-modality">{study.modality_code || '未知'}</span>
                  <span>{study.bodypart_name || '检查部位未知'}</span>
                </span>
                <span className="clinical-record-time">
                  {study.study_time || study.register_time || '检查时间未知'}
                </span>
                <span
                  className="clinical-record-number"
                  title={study.study_id || '未知'}
                >
                  检查号：{study.study_id || '未知'}
                </span>
              </button>
            </li>
          ))}
        </ul>
        <Pagination
          page={page}
          total={result.total}
          busy={busy}
          onPage={changePage}
        />
      </aside>
      <section className="clinical-launch-detail">
        <header>
          <h2>
            {selected
              ? `${selected.chinese_name || '检查详情'} · ${selected.modality_code || ''} ${selected.bodypart_name || ''}`
              : '请选择检查'}
          </h2>
        </header>
        <div
          role="tablist"
          aria-label="检查详情"
          className="clinical-tabs"
        >
          <button
            role="tab"
            aria-selected={tab === 'report'}
            onClick={() => setTab('report')}
            data-cy="clinical-launch-report-tab"
          >
            报告
          </button>
          <button
            role="tab"
            aria-selected={tab === 'image'}
            disabled={!selected || imageBusy || busy}
            onClick={showImages}
            data-cy="clinical-launch-image-tab"
          >
            {imageBusy ? '正在打开…' : '影像'}
          </button>
        </div>
        <ErrorMessage message={error} />
        {selected && (
          <>
            {tab === 'report' && (
              <div
                role="tabpanel"
                className="clinical-launch-report"
              >
                <ReportView
                  registerKey={selected.register_key}
                  api={launchSession.api}
                />
              </div>
            )}
            {imageKey && (
              <div
                role="tabpanel"
                hidden={tab !== 'image'}
                className="clinical-launch-imaging"
              >
                {renderViewer(imageKey)}
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}
